"use client";

import React, { useState, useEffect } from "react";
import { Menu, X, Facebook, Instagram, Youtube, Search } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
const SocialIcon = ({
  href,
  icon: Icon
}: {
  href: string;
  icon: React.ElementType;
}) => <Link to={href} target="_blank" rel="noopener noreferrer" className="text-[#ea0f0f]/80 hover:text-[#fc6e51] transition-colors">
    <Icon className="h-4 w-4" />
  </Link>;
const UtilityLink = ({
  to,
  children
}: {
  to: string;
  children: React.ReactNode;
}) => <Link to={to} className="text-sm font-sans text-[#ea0f0f]/90 hover:text-[#fc6e51] transition-colors hover:underline decoration-[#fc6e51]/50 underline-offset-4">
    {children}
  </Link>;
function NavigationConsumer({
  isScrolled,
  mobileMenuOpen,
  setMobileMenuOpen
}: {
  isScrolled: boolean;
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  const DesktopMenu = () => {
    if (loading) {
      return <div data-section-id="827" className="hidden lg:flex gap-6 animate-pulse">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-20 bg-[#ea0f0f]/10 rounded" />)}
        </div>;
    }
    return <NavigationMenu className="hidden lg:flex">
        <NavigationMenuList className="gap-8">
          {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
              <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className={cn("relative font-sans text-base font-normal tracking-normal transition-colors py-2 block", item.label.toUpperCase() === "HOME" ? "bg-[#fc6e51] text-white px-3 py-1 rounded-sm font-bold after:hidden" : "text-[#ea0f0f] hover:text-[#fc6e51] after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:origin-center after:scale-x-0 after:bg-[#fc6e51] after:transition-transform after:duration-300 hover:after:scale-x-100")}>
                  {item.label}
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>)}
        </NavigationMenuList>
      </NavigationMenu>;
  };
  const MobileMenu = () => {
    if (!mobileMenuOpen) return null;
    return <div className="fixed inset-0 top-[calc(40px+64px)] z-40 bg-[#e0f2f2] border-t border-[#ea0f0f]/10 lg:hidden flex flex-col p-6 overflow-y-auto animate-in slide-in-from-top-5 fade-in duration-200">
        <nav className="flex flex-col gap-6">
          {loading ? <div className="flex flex-col gap-4 animate-pulse">
              {[1, 2, 3, 4].map(i => <div key={i} className="h-6 w-32 bg-[#ea0f0f]/10 rounded" />)}
            </div> : menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className={cn("text-xl font-sans font-bold border-b border-[#ea0f0f]/10 pb-2", item.label.toUpperCase() === "HOME" ? "text-[#fc6e51]" : "text-[#ea0f0f] hover:text-[#fc6e51]")} onClick={() => setMobileMenuOpen(false)} data-index={i}>
                {item.label}
              </Link>)}
          {}
          <div className="mt-4 flex flex-col gap-4 pt-4 border-t border-[#ea0f0f]/10">
            <Link to="#" className="text-[#ea0f0f]/70 hover:text-[#fc6e51] font-sans text-base">
              Updates
            </Link>
            <Link to="#" className="text-[#ea0f0f]/70 hover:text-[#fc6e51] font-sans text-base">
              Roadmap
            </Link>
            <Link to="#" className="text-[#ea0f0f]/70 hover:text-[#fc6e51] font-sans text-base">
              Contact
            </Link>
          </div>
        </nav>
      </div>;
  };
  return <>
      <DesktopMenu />
      <MobileMenu />
    </>;
}
export default function SiteNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className="w-full sticky top-0 z-50 flex flex-col">
      {}
      <div className="bg-[#e0f2f2] h-10 w-full relative z-50 border-b border-[#ea0f0f]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center justify-between">
          {}
          <div className="flex items-center gap-6">
            <UtilityLink to="#">Updates</UtilityLink>
            <UtilityLink to="#">Roadmap</UtilityLink>
            <UtilityLink to="#">Contact</UtilityLink>
          </div>

          {}
          <div className="flex items-center gap-4">
            <SocialIcon href="https://facebook.com" icon={Facebook} />
            <SocialIcon href="https://instagram.com" icon={Instagram} />
            <SocialIcon href="https://youtube.com" icon={Youtube} />
          </div>
        </div>
      </div>

      {}
      <div className={cn("w-full transition-all duration-300 border-b border-transparent", isScrolled ? "bg-[#e0f2f2]/90 backdrop-blur-md border-[#ea0f0f]/10 shadow-sm py-2" : "bg-[#e0f2f2] py-4")}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          {}
          <Link to="/HomePage" className="flex-shrink-0 relative z-50">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1597246/img/campushub_logo.png" alt="AI looks at AI Logo" className="h-10 w-auto object-contain" />
          </Link>

          {}
          <div className="flex items-center gap-6">
            <MenuProvider menu_id="16">
              <NavigationConsumer isScrolled={isScrolled} mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
            </MenuProvider>

            {}
            <Button variant="ghost" size="icon" className="hidden lg:flex text-[#ea0f0f] hover:text-[#fc6e51] hover:bg-transparent">
              <Search className="h-5 w-5" />
              <span className="sr-only">Search</span>
            </Button>

            {}
            <Button variant="ghost" size="icon" className="lg:hidden text-[#ea0f0f]" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              <span className="sr-only">Toggle menu</span>
            </Button>
          </div>
        </div>
      </div>
    </header>;
}